/**
 * treatrx.encounterList.ts
 *
 * Helpers for consuming the TreatRx middleware's `getEncounterList` response.
 *
 * That endpoint returns `json_encode([$html])` (APIController::getEncounterList),
 * i.e. a JSON array holding one markup string — so the raw body arrives with
 * `"` escaped as `\"` and `</option>` as `<\/option>`. Assigning it straight to
 * innerHTML makes the parser read `id` as the literal `\"encounterSelect\"`,
 * which is why the select could never be found. Unwrap the envelope first.
 *
 * Several TreatRx endpoints also `die($message)` on failure, which returns the
 * raw text (including SQL) with HTTP 200 — so a 200 body has to be sniffed
 * before it is trusted as JSON.
 */

/** Unwrap the JSON envelope `["<markup>"]` into usable markup. */
export function unwrapEncounterListMarkup(text: string): string {
  const raw = text ?? '';
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'string') return parsed;
    if (Array.isArray(parsed) && typeof parsed[0] === 'string') return parsed[0];
  } catch {
    // Not JSON — a deployment returning bare markup is used as-is.
  }
  return raw;
}

export type EncounterAction =
  | { kind: 'load'; encounterId: string }
  | { kind: 'create' }
  | { kind: 'unreadable' };

/**
 * Decide what to do with the rendered encounter list.
 *
 * The select is located by tag rather than by `#encounterSelect` so a mangled
 * id can never be mistaken for "this patient has no encounters" — that
 * conflation is what auto-created a duplicate Bravado encounter on every
 * provider selection.
 */
export function chooseEncounterAction(container: HTMLElement): EncounterAction {
  const select = container.querySelector('select');
  if (!select) return { kind: 'unreadable' };

  // The server always appends `<option value="">None</option>`, so a list whose
  // every option has an empty value means the patient has no encounters yet.
  const hasRealEncounter = Array.from(select.options).some((opt) => opt.value !== '');
  if (!hasRealEncounter) return { kind: 'create' };

  // The server marks the first real option `selected`.
  const value = select.value || Array.from(select.options).find((o) => o.value !== '')?.value || '';
  return value === '' ? { kind: 'create' } : { kind: 'load', encounterId: value };
}

/**
 * True when a 2xx body is actually a PHP `die()`/error dump rather than a
 * payload. Mirrors the die() sites in TreatRx's APIController.
 */
export function looksLikePhpErrorBody(text: string): boolean {
  const body = (text ?? '').trim();
  if (body === '') return false;
  return (
    body.startsWith('SQLSTATE[') ||
    body.startsWith('AyvaPatientID is null') ||
    body.startsWith('No doctor found') ||
    /\bFatal error\b/.test(body) ||
    /\bUncaught\b/.test(body) ||
    /\bSQLSTATE\[\d+\]/.test(body)
  );
}

/**
 * Map a TreatRx failure body to something a clinical user can act on. The raw
 * text (which can contain SQL) is logged by the caller, never rendered.
 */
export function friendlyTreatRxError(text: string): string {
  const body = (text ?? '').trim();
  if (body === '') return 'TreatRx request failed.';

  if (/ayvaEncounterID'? cannot be null/i.test(body)) {
    return 'TreatRx did not return an encounter for this patient, so no encounter could be created. Please contact support.';
  }
  if (body.startsWith('AyvaPatientID is null')) {
    return 'This patient is not linked to TreatRx yet. Reload the section, then contact support if it persists.';
  }
  if (body.startsWith('No doctor found')) {
    return 'The selected provider is not set up in TreatRx.';
  }
  if (looksLikePhpErrorBody(body)) {
    return 'TreatRx returned an error. Please contact support.';
  }
  return body;
}
